<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { RepresentativeForm } from '@/api/representatives';

const { t } = useI18n();

interface Props {
  representativeForm: RepresentativeForm;
  errors?: Record<string, string | undefined>;
  errorPrefix?: string;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue']);

function updateField(field: keyof RepresentativeForm, value: RepresentativeForm[keyof RepresentativeForm]): void {
  emit('update:modelValue', { ...props.representativeForm, [field]: value });
}

</script>

<template>
  <v-text-field
    :model-value="representativeForm.firstName"
    variant="outlined"
    :label="`${t('patient.representativeFirstName')} (${t('common.required')})`"
    :error-messages="errors?.[`${errorPrefix || ''}firstName` as keyof typeof errors]"
    @update:model-value="(v: string) => updateField('firstName', v)"
  />
  <v-text-field
    :model-value="representativeForm.lastName"
    variant="outlined"
    :label="`${t('patient.representativeLastName')} (${t('common.required')})`"
    :error-messages="errors?.[`${errorPrefix || ''}lastName` as keyof typeof errors]"
    @update:model-value="(v: string) => updateField('lastName', v)"
  />
  <v-text-field
    :model-value="representativeForm.email"
    variant="outlined"
    :label="t('patient.representativeEmail')"
    :error-messages="errors?.[`${errorPrefix || ''}email` as keyof typeof errors]"
    @update:model-value="(v: string) => updateField('email', v)"
  />
  <p
    v-if="representativeForm.usesApp && !representativeForm.passwordIsSet"
    class="mb-4 text-body-2 text-medium-emphasis"
  >
    {{ t('patient.representativeUsesApp') }}
  </p>
  <v-checkbox
    v-else-if="!representativeForm.passwordIsSet"
    :model-value="representativeForm.sendAppAccess ?? false"
    color="primary"
    class="mb-4"
    :label="t('patient.representativeSendAppAccess')"
    :hint="t('patient.representativeSendAppAccessHint')"
    :disabled="!representativeForm.email"
    persistent-hint
    @update:model-value="(v: boolean | null) => updateField('sendAppAccess', Boolean(v))"
  />
  <v-checkbox
    :model-value="representativeForm.receivesCharges ?? false"
    color="primary"
    class="mb-4"
    data-test="receives-charges"
    :label="t('patient.representativeReceivesCharges')"
    :hint="t('patient.representativeReceivesChargesHint')"
    :disabled="!representativeForm.email"
    persistent-hint
    @update:model-value="(v: boolean | null) => updateField('receivesCharges', Boolean(v))"
  />
  <v-text-field
    :model-value="representativeForm.phoneNumber"
    variant="outlined"
    :label="t('patient.representativePhoneNumber')"
    :error-messages="errors?.[`${errorPrefix || ''}phoneNumber` as keyof typeof errors]"
    @update:model-value="(v: string) => updateField('phoneNumber', v)"
  />
  <v-text-field
    :model-value="representativeForm.dni"
    variant="outlined"
    :label="t('patient.representativeDni')"
    :error-messages="errors?.[`${errorPrefix || ''}dni` as keyof typeof errors]"
    @update:model-value="(v: string) => updateField('dni', v)"
  />
  <span class="text-caption ml-4">{{ t('patient.representativeObservationsInfo') }}</span>
  <v-textarea
    :model-value="representativeForm.observations"
    variant="outlined"
    :label="`${t('patient.representativeObservations')} (${t('common.optional')})`"
    :error-messages="errors?.[`${errorPrefix || ''}observations` as keyof typeof errors]"
    class="mt-4"
    auto-grow
    rows="3"
    @update:model-value="(v: string | undefined) => updateField('observations', v)"
  />
</template>
